import React from "react";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import { useLocation } from "react-router-dom";

import PageHeader from "../../../Components/PageHeader/PageHeader";
import useContact from "../../../hooks/PortalAluno/useContact";
import MenuPageBackLink from "../MenuShared/MenuPageBackLink";
import "./styles.css";

interface ContactLocationState {
  subject?: string;
}

const ContactPage = () => {
  const location = useLocation();
  const locationState = location.state as ContactLocationState | null;
  const { auth, isLoading, mutateAsync } = useContact();
  const [subject, setSubject] = React.useState(locationState?.subject ?? "");
  const [message, setMessage] = React.useState("");
  const [formError, setFormError] = React.useState("");
  const [isSent, setIsSent] = React.useState(false);

  React.useEffect(() => {
    if (locationState?.subject) setSubject(locationState.subject);
  }, [locationState?.subject]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!subject.trim() || !message.trim()) {
      setFormError("Preencha o assunto e a mensagem.");
      return;
    }

    setFormError("");
    setIsSent(false);
    try {
      await mutateAsync({ message, subject });
      setMessage("");
      setIsSent(true);
    } catch (error) {
      setFormError(
        error instanceof Error && error.message !== "Network Error"
          ? error.message
          : "Não foi possível enviar sua mensagem. Verifique sua conexão e tente novamente."
      );
    }
  };

  return (
      <section className="content-page contact-page" aria-label="Fale Conosco">
          <MenuPageBackLink />

          <PageHeader
              title="Fale Conosco"
              description="Envie dúvidas, sugestões ou solicitações para o time responsável."
          />

          <div className="contact-page__layout">
              <aside className="contact-page__info">
                  <span className="contact-page__info-icon" aria-hidden="true">
                      <EmailOutlinedIcon />
                  </span>
                  <h2>Estamos aqui para ajudar</h2>
                  <p>
                      Olá, utilize este espaço para entrar em contato conosco,
                      tirar dúvidas, dicas e sugestões
                  </p>
              </aside>

              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                  {isSent && (
                      <div className="contact-form__success" role="status">
                          <CheckCircleOutlineRoundedIcon aria-hidden="true" />
                          <div>
                              <strong>Mensagem enviada com sucesso!</strong>
                              <span>
                                  Você receberá o retorno em seu e-mail
                                  cadastrado.
                              </span>
                          </div>
                      </div>
                  )}

                  <label htmlFor="contact-subject">Assunto</label>
                  <input
                      id="contact-subject"
                      value={subject}
                      onChange={(event) => {
                          setSubject(event.target.value);
                          setFormError("");
                          setIsSent(false);
                      }}
                      placeholder="Digite o assunto"
                      maxLength={180}
                      aria-invalid={Boolean(formError && !subject.trim())}
                  />

                  <label htmlFor="contact-message">Mensagem</label>
                  <textarea
                      id="contact-message"
                      value={message}
                      onChange={(event) => {
                          setMessage(event.target.value);
                          setFormError("");
                          setIsSent(false);
                      }}
                      placeholder="Escreva sua mensagem"
                      rows={8}
                      maxLength={4000}
                      aria-invalid={Boolean(formError && !message.trim())}
                  />
                  <span className="contact-form__counter">
                      {message.length}/4000
                  </span>

                  {formError && (
                      <p className="contact-form__error" role="alert">
                          {formError}
                      </p>
                  )}

                  <button type="submit" disabled={isLoading}>
                      <SendRoundedIcon aria-hidden="true" />
                      <span>{isLoading ? "Enviando…" : "Enviar mensagem"}</span>
                  </button>
              </form>
          </div>
      </section>
  );
};

export default ContactPage;
